/**
 * نقش‌های تخته‌ی مشترک: هر مرحله یک نقش است که از فلش‌ها ساخته می‌شود — دل، پروانه، ایوان، لاله، بته‌جقه،
 * برجِ آزادی و... (کاربر، ۷ آبان: «چند مرحله با شکل‌ها و نقشه‌های زیاد»).
 *
 * انصاف از قرینه می‌آید (shared.ts): فقط سهمِ بازیکنِ اول کشیده می‌شود و سهمِ بقیه چرخش یا آینه‌ی
 * همان است. پس هر نقش یا خودش قرینه است (دل، ایوان: نیمه‌ی چپ و نیمه‌ی راست) یا از چند نقشِ
 * یکسان دورِ وسط ساخته می‌شود (چهار لاله، چهار ماهی). نقشه‌ی واقعیِ یک جا، مثلِ نقشه‌ی ایران،
 * قرینه ندارد و نمی‌شود منصفانه میانِ رنگ‌ها بخشش کرد؛ برای همین این‌جا نیست.
 *
 * مختصات: تخته از −۱ تا ۱ در هر دو سو، u رو به راست و v رو به پایین (مثلِ صفحه). نقش فقط سهمِ
 * بازیکنِ اول را می‌گوید و به برشِ خودش بریده می‌شود؛ بیرون‌زدگی از برش مهم نیست.
 */

import { Rng } from "./rng";

export type Shape = (u: number, v: number) => boolean;

/**
 * قرینه: rot4 چهار برشِ گرداگردِ وسط (برشِ بالا مالِ بازیکنِ اول)، mir4 چهار ربع که آینه‌ی هم‌اند
 * (بالا-چپ)، mir2 نیمه‌ی چپ و راست، rot2 نیمه‌ی بالا و پایین که چرخشِ ۱۸۰ درجه‌ی هم‌اند.
 * با دو بازیکن روی rot4 و mir4، هر کس دو برشِ کنارِ هم دارد.
 */
export type Sym = "rot4" | "mir4" | "mir2" | "rot2";

/** نقشِ وسط که فلش ندارد: شمسه (دایره)، ترنج (لوزی) یا تنِ پروانه (باریکه). rx و ry نیم‌پهنا و نیم‌بلندا. */
export interface Hub { kind: "shamseh" | "toranj" | "body"; rx: number; ry: number }

export interface Picture {
  id: string;
  sym: Sym;
  part: Shape;
  hub?: Hub;
}

// ---- شکل‌های پایه ----

const circle = (cx: number, cy: number, r: number): Shape => (u, v) => (u - cx) ** 2 + (v - cy) ** 2 <= r * r;

/** بیضی، چرخیده به اندازه‌ی deg درجه (ساعت‌گرد روی صفحه). */
function ellipse(cx: number, cy: number, rx: number, ry: number, deg = 0): Shape {
  const a = (deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  return (u, v) => {
    const x = u - cx, y = v - cy;
    return ((x * c + y * s) / rx) ** 2 + ((-x * s + y * c) / ry) ** 2 <= 1;
  };
}

const box = (x0: number, y0: number, x1: number, y1: number): Shape => (u, v) => u >= x0 && u <= x1 && v >= y0 && v <= y1;

/** چندضلعی از جفت‌های u, v پشتِ هم. */
function poly(...xy: number[]): Shape {
  const n = xy.length / 2;
  return (u, v) => {
    let inside = false;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = xy[2 * i]!, yi = xy[2 * i + 1]!, xj = xy[2 * j]!, yj = xy[2 * j + 1]!;
      if (yi > v !== yj > v && u < ((xj - xi) * (v - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
}

const any = (...s: Shape[]): Shape => (u, v) => s.some((f) => f(u, v));
const cut = (a: Shape, ...holes: Shape[]): Shape => (u, v) => a(u, v) && !holes.some((f) => f(u, v));

/** نوارِ پهنی که در درازایش پهن و باریک می‌شود: hw(t) نیم‌پهنا، t از ۰ (v0) تا ۱ (v1)، رو به بالا یا پایین. */
const band = (v0: number, v1: number, hw: (t: number) => number): Shape => (u, v) => {
  const t = (v - v0) / (v1 - v0);
  return t >= 0 && t <= 1 && Math.abs(u) <= hw(t);
};

/** دُمی که از p0 تا p2 (با خمِ p1) باریک می‌شود: پشتِ هم دایره، از r0 تا r1. */
function tail(p0: [number, number], p1: [number, number], p2: [number, number], r0: number, r1: number): Shape {
  const dots: Shape[] = [];
  for (let k = 0; k <= 24; k++) {
    const t = k / 24, a = (1 - t) ** 2, b = 2 * t * (1 - t), c = t * t;
    dots.push(circle(a * p0[0] + b * p1[0] + c * p2[0], a * p0[1] + b * p1[1] + c * p2[1], r0 + (r1 - r0) * t));
  }
  return any(...dots);
}

/** دل: دو دایره و یک سه‌گوش. نوکش رو به پایین (down) یا بالا. */
function heart(cx: number, cy: number, s: number, down = true): Shape {
  const k = down ? 1 : -1;
  return any(
    circle(cx - 0.42 * s, cy - 0.3 * s * k, 0.47 * s),
    circle(cx + 0.42 * s, cy - 0.3 * s * k, 0.47 * s),
    poly(cx - 0.86 * s, cy - 0.08 * s * k, cx + 0.86 * s, cy - 0.08 * s * k, cx, cy + 0.92 * s * k),
  );
}

/** ستاره با n پر در زاویه‌های a0 + k·۳۶۰/n (درجه، صفر رو به راست، ساعت‌گرد)، از شعاعِ rin تا rout. */
function star(n: number, a0: number, rin: number, rout: number): Shape {
  const step = 360 / n;
  return (u, v) => {
    const r = Math.hypot(u, v);
    const a = (((Math.atan2(v, u) * 180) / Math.PI - a0) % step + step) % step;
    const off = Math.min(a, step - a) / (step / 2);
    return r <= rin + (rout - rin) * (1 - off);
  };
}

/** کمانِ تیزِ ایرانی (جناغی) بر پایه‌ی v0 با نیم‌پهنای a، تا بلندیِ h بالاتر — و هر چه زیرِ پایه تا v1. */
function arch(a: number, v0: number, h: number, v1: number): Shape {
  const d = (h * h - a * a) / (2 * a), R = a + d;
  return (u, v) => {
    if (v >= v0) return v <= v1 && Math.abs(u) <= a;
    return (u - d) ** 2 + (v - v0) ** 2 <= R * R && (u + d) ** 2 + (v - v0) ** 2 <= R * R;
  };
}

// ---- نقش‌ها ----

const SHAMSEH = (r = 0.24): Hub => ({ kind: "shamseh", rx: r, ry: r });

export const PICTURES = {
  // دو نفر — نیمه‌ی چپ مالِ من، نیمه‌ی راست آینه‌اش.
  khesht: { id: "khesht", sym: "mir2", part: (u, v) => (Math.abs(u) / 0.66) ** 0.85 + (Math.abs(v) / 0.96) ** 0.85 <= 1 },
  del: { id: "del", sym: "mir2", part: heart(0, -0.04, 1) },
  pik: {
    id: "pik", sym: "mir2",
    part: any(heart(0, 0.02, 0.92, false), poly(-0.09, 0.4, 0.09, 0.4, 0.34, 0.95, -0.34, 0.95)),
  },
  gashniz: {
    id: "gashniz", sym: "mir2",
    part: any(circle(0, -0.5, 0.34), circle(-0.45, 0.06, 0.34), circle(0.45, 0.06, 0.34), circle(0, -0.04, 0.22),
      poly(-0.09, 0.2, 0.09, 0.2, 0.34, 0.95, -0.34, 0.95)),
  },
  mahi2: {
    // دو ماهیِ سفره‌ی هفت‌سین، سر و ته: ماهیِ بالا رو به راست، ماهیِ پایین چرخشِ همان.
    id: "mahi2", sym: "rot2",
    part: cut(any(ellipse(0.08, -0.5, 0.56, 0.3), poly(-0.38, -0.5, -0.9, -0.84, -0.9, -0.16),
      poly(-0.06, -0.74, 0.22, -0.74, -0.2, -0.95)), circle(0.4, -0.58, 0.075)),
  },
  parvaneh: {
    id: "parvaneh", sym: "mir2", hub: { kind: "body", rx: 0.075, ry: 0.66 },
    part: cut(any(poly(-0.08, -0.1, -0.3, -0.75, -0.62, -0.96, -0.92, -0.84, -0.96, -0.45, -0.76, -0.12, -0.4, 0),
      poly(-0.08, 0.06, -0.45, 0.1, -0.72, 0.3, -0.72, 0.62, -0.46, 0.84, -0.2, 0.74, -0.08, 0.42)),
    circle(-0.6, -0.56, 0.12), circle(-0.44, 0.46, 0.09)),
  },
  anar: {
    id: "anar", sym: "mir2",
    part: any(circle(0, 0.18, 0.76), poly(-0.3, -0.5, -0.4, -0.96, -0.15, -0.74, 0, -0.99, 0.15, -0.74, 0.4, -0.96, 0.3, -0.5)),
  },
  taj: {
    id: "taj", sym: "mir2",
    part: cut(any(box(-0.84, 0.32, 0.84, 0.8),
      ...[-0.78, -0.39, 0, 0.39, 0.78].flatMap((x) => [poly(x - 0.2, 0.34, x + 0.2, 0.34, x, -0.42), circle(x, -0.5, 0.13)])),
    circle(-0.42, 0.56, 0.085), circle(0.42, 0.56, 0.085), circle(0, 0.56, 0.1)),
  },
  goldan: {
    id: "goldan", sym: "mir2",
    part: cut(any(box(-0.38, -0.95, 0.38, -0.84), box(-0.2, -0.86, 0.2, -0.44), ellipse(0, 0.12, 0.68, 0.6),
      poly(-0.3, 0.6, 0.3, 0.6, 0.38, 0.95, -0.38, 0.95)), box(-0.9, 0.04, 0.9, 0.11)),
  },
  ivan: {
    // ایوان: پیشانیِ کاشی با درِ جناغی، و طاقِ باریکی دورِ در.
    id: "ivan", sym: "mir2",
    part: cut(box(-0.8, -0.96, 0.8, 0.94), arch(0.3, 0.22, 0.62, 0.94),
      cut(arch(0.5, 0.22, 0.86, 0.94), arch(0.41, 0.22, 0.75, 0.94))),
  },
  azadi: {
    // برجِ آزادی: پایه‌های بازشونده با طاقِ بزرگِ وسط، تنه‌ی باریک و سرِ برج.
    id: "azadi", sym: "mir2",
    part: cut(any(band(-0.52, 0.95, (t) => 0.26 + 0.68 * t ** 1.5),
      poly(-0.34, -0.5, 0.34, -0.5, 0.46, -0.72, 0.3, -0.84, 0, -0.97, -0.3, -0.84, -0.46, -0.72)),
    arch(0.3, 0.45, 0.6, 0.95), box(-0.1, -0.74, 0.1, -0.6)),
  },

  // چهار نفر — نقشِ برشِ بالا، و سه برشِ دیگر چرخشِ همان.
  plus: { id: "plus", sym: "rot4", hub: SHAMSEH(0.26), part: (u) => Math.abs(u) <= 0.26 },
  setareh: { id: "setareh", sym: "rot4", hub: SHAMSEH(), part: (u, v) => Math.abs(u) <= 0.05 + 0.24 * Math.min(1, (1 + v) / 0.8) },
  golbarg: { id: "golbarg", sym: "rot4", hub: SHAMSEH(0.2), part: ellipse(0, -0.6, 0.28, 0.4) },
  badbezan: {
    id: "badbezan", sym: "rot4", hub: SHAMSEH(0.2),
    part: (u, v) => Math.abs(u) <= 0.08 + 0.34 * Math.max(0, -v - 0.2) && v >= -0.98 + 0.25 * (u / 0.4) ** 2,
  },
  shabdar: { id: "shabdar", sym: "rot4", hub: SHAMSEH(0.2), part: heart(0, -0.62, 0.38) },
  mahiha: {
    id: "mahiha", sym: "rot4", hub: SHAMSEH(0.22),
    part: cut(any(ellipse(0.16, -0.72, 0.42, 0.22), poly(-0.18, -0.72, -0.48, -0.95, -0.48, -0.53), poly(0.0, -0.9, 0.22, -0.9, -0.12, -1)),
      circle(0.42, -0.77, 0.055)),
  },
  gorbeh: {
    id: "gorbeh", sym: "rot4", hub: SHAMSEH(0.2),
    part: cut(any(circle(0, -0.64, 0.28), poly(-0.28, -0.7, -0.06, -0.88, -0.26, -1), poly(0.28, -0.7, 0.06, -0.88, 0.26, -1)),
      ellipse(-0.11, -0.68, 0.05, 0.075), ellipse(0.11, -0.68, 0.05, 0.075)),
  },
  laleh: {
    // لاله‌ی کاشی: جامِ سه‌پر، ساقه‌ی یک‌خانه‌ای رو به وسط و دو برگ.
    id: "laleh", sym: "rot4", hub: SHAMSEH(0.2),
    part: any(ellipse(0, -0.72, 0.24, 0.19), poly(-0.24, -0.74, -0.27, -1, -0.09, -0.86, 0, -1, 0.09, -0.86, 0.27, -1, 0.24, -0.74),
      box(-0.035, -0.62, 0.035, -0.2), ellipse(-0.15, -0.42, 0.14, 0.055, -38), ellipse(0.15, -0.42, 0.14, 0.055, 38)),
  },
  mahha: { id: "mahha", sym: "rot4", hub: SHAMSEH(0.2), part: cut(circle(0, -0.62, 0.34), circle(0, -0.82, 0.28)) },
  boteh: {
    id: "boteh", sym: "rot4", hub: SHAMSEH(0.2),
    part: cut(any(circle(0, -0.5, 0.23), tail([-0.08, -0.6], [-0.3, -0.98], [0.24, -0.92], 0.2, 0.03)), circle(0.01, -0.5, 0.085)),
  },
  hashtpar: { id: "hashtpar", sym: "rot4", hub: SHAMSEH(0.26), part: star(8, 22.5, 0.5, 1) },
  halgheh: { id: "halgheh", sym: "mir4", part: (u, v) => Math.hypot(u, v) >= 0.56 && Math.hypot(u, v) <= 0.96 },
  lachak: {
    // فرشِ لچک‌ترنج: حاشیه و لچکِ گوشه‌ها فلش‌اند، ترنجِ وسط نقشِ ثابت.
    id: "lachak", sym: "mir4", hub: { kind: "toranj", rx: 0.4, ry: 0.56 },
    part: any((u, v) => u <= -0.82 || v <= -0.82, cut(circle(-1, -1, 0.66), circle(-1, -1, 0.3))),
  },
} satisfies Record<string, Picture>;

export type PictureId = keyof typeof PICTURES;

/** برشِ بازیکنِ اول: نقش فقط در همین برش فلش می‌گیرد. dx و dy فاصله‌ی خانه از وسطِ تخته (به خانه). */
export function inSlice(sym: Sym, dx: number, dy: number): boolean {
  if (sym === "rot4") return -dy > Math.abs(dx) || (dx === -dy && dx > 0);
  if (sym === "rot2") return dy < 0 || (dy === 0 && dx > 0);
  if (sym === "mir2") return dx < 0;
  return dx < 0 && dy < 0;
}

export function inHub(h: Hub, u: number, v: number): boolean {
  if (h.kind === "shamseh") return (u / h.rx) ** 2 + (v / h.ry) ** 2 <= 1;
  if (h.kind === "toranj") return Math.abs(u) / h.rx + Math.abs(v) / h.ry <= 1;
  return Math.abs(u) <= h.rx && Math.abs(v) <= h.ry;
}

// ---- نقشه‌های ساختگی: مرحله‌های بی‌پایان (کاربر، ۷ آبان: «بازی باید بی‌پایان باشد، مرحله و نقشه‌ی زیاد») ----

/**
 * نقشه‌ی شماره‌ی seed: چند شکلِ ساده (دایره، بیضی، حلقه، لوزی) در برشِ بازیکنِ اول، و آینه‌ی همان‌ها در خودِ برش —
 * پس روی تخته هم قرینه‌ی برش‌هاست و هم قرینه‌ی درونِ هر برش، و نقش مثلِ کاشی و شمسه درمی‌آید. هر عدد یک نقشه‌ی
 * تازه می‌دهد و همان عدد همیشه همان نقشه را؛ عددها از SplitMix64 اند و حسابشان ساده، پس Go و C# عیناً همین را
 * می‌سازند (testdata/arrows_vectors.json).
 *
 * syms: قرینه‌های مجاز (چهار نفر فقط چهاربرشی‌ها).
 */
export function mapPicture(seed: number, syms: readonly Sym[]): Picture {
  const rng = new Rng(seed + 0x6d6170);
  const sym = syms[rng.next(syms.length)]!;
  // آینه‌ی درونِ برش: برشِ بالا و نیمه‌ی بالا روی محورِ عمودی، نیمه‌ی چپ روی محورِ افقی، ربع روی قطر.
  const fold = (u: number, v: number): [number, number] =>
    sym === "rot4" || sym === "rot2" ? [-u, v] : sym === "mir2" ? [u, -v] : [v, u];
  const inside = (u: number, v: number) =>
    sym === "rot4" ? -v > Math.abs(u) : sym === "rot2" ? v < 0 : sym === "mir2" ? u < 0 : u < 0 && v < 0;
  const val = (a: number, b: number) => a + (b - a) * rng.value();

  const shapes: Shape[] = [];
  const holes: Shape[] = [];
  const add = () => {
    // مرکز در برش و دور از وسط (جای نقشِ وسط)؛ با چند بار کوشش، وگرنه همان آخری.
    let cx = 0, cy = -0.6;
    for (let k = 0; k < 24; k++) {
      cx = val(-1, 1); cy = val(-1, 1);
      const r = Math.sqrt(cx * cx + cy * cy);
      if (inside(cx, cy) && r >= 0.32 && r <= 0.9) break;
    }
    const kind = rng.next(4);
    let f: Shape;
    if (kind === 0) f = circle(cx, cy, val(0.13, 0.27));
    else if (kind === 1) f = ellipse(cx, cy, val(0.16, 0.36), val(0.08, 0.16), val(0, 180));
    else if (kind === 2) { const r = val(0.15, 0.28); f = cut(circle(cx, cy, r), circle(cx, cy, r * 0.5)); }
    else {
      const a = val(0.13, 0.26), b = val(0.15, 0.32), t = (val(0, 90) * Math.PI) / 180, c = Math.cos(t), s = Math.sin(t);
      f = (u, v) => { const x = u - cx, y = v - cy; return Math.abs(x * c + y * s) / a + Math.abs(-x * s + y * c) / b <= 1; };
    }
    shapes.push(f);
  };
  for (let k = 3 + rng.next(3); k > 0; k--) add();
  if (rng.chance(0.5)) {
    const [hx, hy] = [val(-0.6, 0.6), val(-0.85, -0.4)];
    if (inside(hx, hy)) holes.push(circle(hx, hy, val(0.04, 0.08)));
  }
  const body = (u: number, v: number) => shapes.some((f) => f(u, v));
  const part: Shape = (u, v) => {
    if (holes.some((f) => f(u, v))) return false;
    const [fu, fv] = fold(u, v);
    return body(u, v) || body(fu, fv);
  };
  // نقشه‌ی کم‌خانه (شکل‌ها روی هم افتادند) شکلِ دیگری می‌گیرد، تا نقش پیدا باشد.
  const area = () => {
    let n = 0;
    for (let y = 0; y < 40; y++) for (let x = 0; x < 40; x++) {
      const u = (x + 0.5) / 20 - 1, v = (y + 0.5) / 20 - 1;
      if (inside(u, v) && part(u, v)) n++;
    }
    return n;
  };
  const slice = sym === "rot4" || sym === "mir4" ? 400 : 800;
  for (let k = 0; k < 8 && area() < slice * 0.24; k++) add();

  let hub: Hub | undefined;
  if (sym === "rot4" && rng.chance(0.8)) { const r = val(0.16, 0.26); hub = { kind: "shamseh", rx: r, ry: r }; }
  else if (sym === "mir4" && rng.chance(0.5)) hub = { kind: "toranj", rx: val(0.28, 0.4), ry: val(0.4, 0.56) };
  return { id: `map${seed}`, sym, part, hub };
}

/** نام‌های نقشه‌های ساختگی — واژه‌های هنرِ ایرانی؛ شماره‌ی نقشه یکی را برمی‌دارد. */
export const MAP_NAMES = [
  "کاشی", "گنبد", "باغ", "چشمه", "گلستان", "فیروزه", "لاجورد", "ترمه", "نگار", "پرگار", "محراب", "شبستان",
  "رواق", "مقرنس", "اسلیمی", "ختایی", "گره", "سنتور", "قلمدان", "آینه‌کاری", "کهکشان", "پیچک", "نسیم", "مهتاب",
];
